/**
 * @file 「加入知识库」— UseFormSearch 字段配置
 * label 留空（视觉 label 在各 Field 内自绘），避免 tea Form.Item label 错位挡点击
 */
import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import type { IConfig } from '@gp/pages/Pipeline/ProjectDetails/components/GameOverviewEdit/Components/UseFormSearch';
import type {
  IKnowledgeBaseItem,
  ITeamOption,
} from '@/pages/aigc/pages/Agent/components/QAInput/KnowledgeBaseMention/hooks/useKnowledgeBaseApi';
import {
  KbAiToggleField,
  KbSelectField,
  KbSpaceField,
  KbTeamSelectField,
  KbTextAreaField,
  KbContentEditableField,
  KbTextInput,
  type TKnowledgeSpace,
} from '../components/KbAddFormControls';
import { isEditableHtmlEmpty } from '../utils';
import { EMPTY_LABEL, createRequiredValidator, createTeamIdValidator } from '../utils/addModalSpaceUtils';

/** 加入知识库弹窗 Tab 类型：已有知识库 / 新建知识库 */
export type TKnowledgeAddTab = 'existing' | 'new';

/**
 * useKnowledgeBaseAddFormConfig hook 选项
 * @interface IUseKnowledgeBaseAddFormConfigOptions
 */
export interface IUseKnowledgeBaseAddFormConfigOptions {
  /** 当前 Tab */
  activeTab: TKnowledgeAddTab;
  /** 空间类型：个人 / 团队 */
  space: TKnowledgeSpace;
  /** 空间变更回调 */
  onSpaceChange?: (space: TKnowledgeSpace) => void;
  /** 团队切换回调（用于外部刷新知识库列表） */
  onTeamChange?: (teamId: string) => void;
  /** 知识库列表选项 */
  kbOptions?: IKnowledgeBaseItem[];
  /** 知识库列表加载中 */
  kbLoading?: boolean;
  /** 当前空间下无可用知识库（展示名称输入并走新建） */
  kbOptionsEmpty?: boolean;
  /** 团队选项列表 */
  teamOptions?: ITeamOption[];
  /** 团队空间列表是否为空（无可用团队），为 true 时禁用团队 Radio */  
  hasTeamSpaces?: boolean;
  /** 划词 HTML，正文 contentEditable 初始渲染 */
  contentHtml?: string;
}

/**
 * 加入知识库表单配置 Hook
 * @param options - hook 选项
 * @returns UseFormSearch 的 config 数组
 */
export function useKnowledgeBaseAddFormConfig(
  options: IUseKnowledgeBaseAddFormConfigOptions,
): { config: IConfig[]; } {
  const { formatMessage } = useIntl();
  const {
    activeTab,
    space,
    onSpaceChange,
    onTeamChange,
    kbOptions = [],
    kbLoading = false,
    kbOptionsEmpty = false,
    teamOptions = [],
    hasTeamSpaces = true,
    contentHtml = '',
  } = options;

  // 表单只需 id/name；忽略 docs 合并带来的引用变化，避免下拉打开时 config remount 导致点选失效
  const kbSelectOptions = useMemo(
    () => kbOptions.map((item) => ({ value: item.kbId, text: item.kbName })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kbOptions.map((item) => `${item.kbId}\0${item.kbName}`).join('\n')],
  );

  const teamSelectOptions = useMemo(
    () => teamOptions.map((item) => ({ value: item.value, text: item.text })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamOptions.map((item) => `${item.value}\0${item.text}`).join('\n')],
  );

  const config = useMemo((): IConfig[] => {
    const titleRequiredMsg = formatMessage({ id: 'dba_kb_add_title_required' });
    const contentRequiredMsg = formatMessage({ id: 'dba_kb_add_content_required' });
    const kbRequiredMsg = formatMessage({ id: 'dba_kb_add_kb_required' });
    const kbNameRequiredMsg = formatMessage({ id: 'dba_kb_add_kb_name_required' });
    const teamRequiredMsg = formatMessage({ id: 'dba_kb_add_team_required' });
    const validateTeamId = createTeamIdValidator(teamRequiredMsg);

    const fields: IConfig[] = [
      {
        name: 'space',
        label: EMPTY_LABEL,
        component: <KbSpaceField onSpaceChange={onSpaceChange} hasTeamSpaces={hasTeamSpaces} />,
        rules: { required: false, message: '' },
      },
    ];

    if (space === 'team') {
      fields.push({
        name: 'teamId',
        label: EMPTY_LABEL,
        component: (
          <KbTeamSelectField
            label={formatMessage({ id: 'dba_kb_add_select_team' })}
            required
            placeholder={formatMessage({ id: 'dba_kb_add_select_team' })}
            options={teamSelectOptions}
            autoSelectFirst
            onTeamChange={onTeamChange}
          />
        ),
        rules: {
          required: true,
          message: teamRequiredMsg,
          validator: validateTeamId,
        },
      });
    }

    if (activeTab === 'existing') {
      if (kbOptionsEmpty) {
        fields.push({
          name: 'kbName',
          label: EMPTY_LABEL,
          component: (
            <KbTextInput
              label={formatMessage({ id: 'dba_kb_add_target_kb' })}
              required
              placeholder={formatMessage({ id: 'dba_kb_add_kb_name_placeholder' })}
            />
          ),
          rules: {
            required: true,
            message: kbNameRequiredMsg,
            validator: createRequiredValidator(kbNameRequiredMsg, 'kbName'),
          },
        });
      } else {
        fields.push({
          name: 'kbId',
          label: EMPTY_LABEL,
          component: (
            <KbSelectField
              label={formatMessage({ id: 'dba_kb_add_target_kb' })}
              required
              placeholder={
                kbLoading
                  ? formatMessage({ id: 'dba_loading' })
                  : formatMessage({ id: 'dba_kb_add_select_kb' })
              }
              options={kbSelectOptions}
              autoSelectFirst
            />
          ),
          rules: {
            required: true,
            message: kbRequiredMsg,
            validator: createRequiredValidator(kbRequiredMsg, 'kbId'),
          },
        });
      }
    } else {
      fields.push(
        {
          name: 'kbName',
          label: EMPTY_LABEL,
          component: (
            <KbTextInput
              label={formatMessage({ id: 'dba_kb_add_kb_name' })}
              required
              placeholder={formatMessage({ id: 'dba_kb_add_kb_name_placeholder' })}
            />
          ),
          rules: {
            required: true,
            message: kbNameRequiredMsg,
            validator: createRequiredValidator(kbNameRequiredMsg, 'kbName'),
          },
        },
        {
          name: 'kbDesc',
          label: EMPTY_LABEL,
          component: (
            <KbTextAreaField
              label={formatMessage({ id: 'dba_kb_add_kb_desc' })}
              placeholder={formatMessage({ id: 'dba_kb_add_kb_desc_placeholder' })}
              rows={8}
            />
          ),
          rules: { required: false, message: '' },
        },
        {
          name: 'aiEnabled',
          label: EMPTY_LABEL,
          component: <KbAiToggleField />,
          rules: { required: false, message: '' },
        },
      );
    }

    fields.push(
      {
        name: 'title',
        label: EMPTY_LABEL,
        component: (
          <KbTextInput
            label={formatMessage({ id: 'dba_kb_add_title' })}
            required
            placeholder={formatMessage({ id: 'dba_kb_add_title_placeholder' })}
          />
        ),
        rules: {
          required: true,
          message: titleRequiredMsg,
          validator: createRequiredValidator(titleRequiredMsg, 'title'),
        },
      },
      {
        name: 'content',
        label: EMPTY_LABEL,
        component: (
          <KbContentEditableField
            key={contentHtml || 'kb-content-empty'}
            label={formatMessage({ id: 'dba_kb_add_content' })}
            required
            placeholder={formatMessage({ id: 'dba_kb_add_content_placeholder' })}
            initialHtml={contentHtml}
          />
        ),
        rules: {
          required: true,
          message: contentRequiredMsg,
          validator: (values: Record<string, unknown>) =>
            (isEditableHtmlEmpty(String(values.content ?? '')) ? contentRequiredMsg : ''),
        },
      },
    );

    return fields;
  }, [
    activeTab,
    space,
    onSpaceChange,
    onTeamChange,
    kbSelectOptions,
    kbLoading,
    kbOptionsEmpty,
    teamSelectOptions,
    hasTeamSpaces,
    contentHtml,
    formatMessage,
  ]);

  return { config };
}
